export const farms = [
  {
    id: "boerderij-roskam",
    name: "Boerderij Roskam",
    location: "Noordoostpolder + Oversijssel, Netherlands",
    region: "East Holland",
    farmerName: "Klaas, Cor, Léon & Miranda Roskam",
    farmerQuote: "Having sheep is a way of living, it is part of who we are, it has shaped who we are.",
    heroImage: "/roskam_farm_four.jpg",
    images: [
      "/roskam_farm_four.jpg",
      "/roskam_farm_one.jpg",
      "/roskam_farm_two.jpg"
    ],
    storyShort: "Around 1985 a father and a son started farming with a few sheep, which slowly grew into a bigger farm of around 150 sheep in 2020. Since 2020 the farm has shrunk down to 65 sheep, but the passion for farming stays exactly the same.",
    storyFull: `Boerderij Roskam is located on the border of Noordoostpolder and Overijssel, with its land spread across two provinces and municipalities. The farm began around 1985, when a father and his son started keeping a small number of sheep. What started as a modest initiative gradually developed into a fully functioning sheep farm.

Over the years, the flock grew steadily through practical experience and daily work. Sheep farming became part of everyday life, shaping routines, responsibilities, and family involvement. Klaas, Cor, Léon, and Miranda all play a role in the farm, each contributing to its continuity and development.

By 2020, the farm had grown to approximately 150 sheep. The flock consists of a mix of Texelaars, Swifters, and Hampshire Down. This breeding combination results in large, strong sheep that are mostly white with a brown head, well suited to the local landscape.

After 2020, changes in circumstances led to a reduction in flock size to around 65 sheep. Despite this decrease, the commitment to sheep farming remained unchanged. 

As the farmers themselves say, “Having sheep is a way of living, it is part of who we are, it has shaped who we are.”. 

Today, Boerderij Roskam continues as a family farm where experience, care for the animals, and dedication to the land remain central.`,
    sheepBreed: "Mix of Texel, Swifters & Hemshire",
    flockSize: "~95 sheep",
    sustainabilityPractices: [
      "Rotational grazing to promote pasture health",
      "No antibiotics in preventive treatment",
      "Solar-powered farm buildings",
      "Local feed sourcing within 20km"
    ],
    certifications: ["Animal Welfare Approved", "Organic in Transition"],
    productsFromFarm: ["heritage-sweater-natural", "coastal-cardigan-slate", "winter-beanie-charcoal"]
  },
  {
    id: "hoeve-het-schaap",
    name: "Hoeve Het Schaap",
    location: "Drenthe, Netherlands",
    region: "Drenthe",
    farmerName: "Familie Bakker",
    farmerQuote: "The Drenthe Heath sheep is our heritage. Their wool may be coarser, but it carries the soul of these ancient lands.",
    heroImage: "https://images.unsplash.com/photo-1588152850700-c82ecb8ba9b1?w=1200&q=80",
    images: [
      "https://images.unsplash.com/photo-1588152850700-c82ecb8ba9b1?w=800&q=80",
      "https://images.unsplash.com/photo-1677851385001-37b04543b6df?w=800&q=80"
    ],
    storyShort: "Preserving the rare Drenthe Heath sheep breed on the misty heathlands of eastern Netherlands, where the Bakker family honors centuries-old pastoral traditions.",
    storyFull: `On the vast heathlands of Drenthe, where morning mist clings to purple heather and ancient burial mounds dot the landscape, the Bakker family tends to one of the Netherlands' rarest sheep breeds: the Drenthe Heath sheep.

"These sheep have grazed these heaths for over a thousand years," explains Henk Bakker, third-generation shepherd at Hoeve Het Schaap. "They're not just livestock—they're living history."

The Drenthe Heath sheep is a small, hardy breed perfectly adapted to the region's sandy, nutrient-poor soils. Their wool is coarser than commercial breeds—historically used for mattresses, carpets, and workwear rather than fine garments. As fashion trends shifted toward softer fibers, demand for their wool collapsed.

"By the 1990s, we were burning the fleeces," Henk admits. "Nobody wanted them. We kept the sheep for conservation, for maintaining the heathland, but their wool had become worthless."

Hoeve Het Schaap partnered with WoolStory to find new applications for this characterful fiber. "They didn't try to make it something it's not," says Henk's daughter Sophie, who manages the farm's WoolStory partnership. "Instead, they found uses where the coarser texture is actually an advantage."

The farm's wool now becomes blankets, cushion covers, and home textiles—products where the wool's natural durability and texture are celebrated rather than disguised. Each piece carries the authentic character of the Drenthe heathlands.

"These sheep help keep the heathland healthy," Sophie explains. "They eat the young trees and brush that would otherwise take over. Now their wool contributes too. It's a complete circle."

The Bakker family maintains a flock of 200 Drenthe Heath sheep, working with local conservation groups to manage over 500 hectares of protected heathland. Their partnership with WoolStory has made the wool economically viable again—ensuring this ancient breed has a future.

"My grandfather would be amazed," Henk says, examining a finished WoolStory blanket made from his sheep's wool. "The same wool he couldn't give away, now traveling to homes across Europe. That's something worth continuing."`,
    sheepBreed: "Drenthe Heath Sheep (Drents Heideschaap)",
    flockSize: "~200 sheep",
    sustainabilityPractices: [
      "Conservation grazing on protected heathland",
      "Heritage breed preservation",
      "Chemical-free land management",
      "Partnership with nature reserves"
    ],
    certifications: ["Recognized Conservation Grazer", "Heritage Breed Guardian"],
    productsFromFarm: ["meadow-blanket-oatmeal", "farmhouse-cushion-cream"]
  },
  {
    id: "schapenboerderij-noord",
    name: "Schapenboerderij Noord",
    location: "Friesland, Netherlands",
    region: "Friesland",
    farmerName: "Pieter & Anna de Jong",
    farmerQuote: "Every fleece tells a different story in color. We've learned to see beauty in what others call imperfect.",
    heroImage: "https://images.unsplash.com/photo-1677851385001-37b04543b6df?w=1200&q=80",
    images: [
      "https://images.unsplash.com/photo-1677851385001-37b04543b6df?w=800&q=80",
      "https://images.unsplash.com/photo-1494079218307-7fa091ab4df2?w=800&q=80"
    ],
    storyShort: "A small Frisian farm celebrating wool's natural color variations, where the de Jong family raises sheep for their diverse, multicolored fleeces.",
    storyFull: `Schapenboerderij Noord sits on the clay soils of northern Friesland, where the de Jong family raises sheep with an unusual focus: color diversity.

"Most farmers breed for uniform white wool," explains Pieter de Jong. "We've gone the opposite direction. We select for variety."

The farm's flock includes Zwartbles, Blue Texels, and various crossbreeds, producing fleeces in white, black, brown, grey, and every shade between. In commercial wool markets, this variety is a liability—mixed colors complicate processing and reduce value. For WoolStory, it's an opportunity.

"When WoolStory first visited, they spent hours looking at fleeces I thought were unsellable," Pieter recalls. "The ones with multiple colors, the ones that weren't quite anything—those were exactly what they wanted."

Anna de Jong manages the farm's wool sorting and preparation. "We send them wool sorted by color family, but with natural variation intact," she explains. "They blend it into yarns that look like the actual sheep—real colors, real variation, not factory-perfect."

The result is WoolStory's Highland collection, where heathered and marled effects come from genuine color diversity rather than artificial dyes. Each scarf or accessory contains wool from multiple sheep, creating truly unique pieces.

"People ask if we can make two identical scarves," Anna laughs. "We can't. That's the point."

The de Jongs maintain a flock of about 150 sheep across multiple breeds. They've become advocates for valuing wool diversity, hosting farm visits and educating other farmers about the potential of colored wool.

"The wool industry pushed uniformity for decades," Pieter reflects. "But uniformity means waste—anything different gets rejected. We're proving different can be better."`,
    sheepBreed: "Mixed (Zwartbles, Blue Texel, crosses)",
    flockSize: "~150 sheep",
    sustainabilityPractices: [
      "Genetic diversity preservation",
      "No-dye wool production",
      "Small-scale, high-attention husbandry",
      "Educational farm visits"
    ],
    certifications: ["Animal Welfare Approved"],
    productsFromFarm: ["highland-scarf-heather"]
  }
];
